import { Box, Stack } from "@mui/material";
import {
  ColorFamily,
  DesignBoard,
  Finishes,
  GreatFor,
  Patterns,
  ProjectCategory,
  Textures,
} from "../../../../__generated__/types";
import { HdgTextInput } from "../../../../components/hdg-text-input";
import { DropdownSelect } from "../../../../components/dropdown-select";
import {
  colorFamilyOptions,
  finishOptions,
  greatForOptions,
  patternOptions,
  projectCategoryOptions,
  textureOptions,
} from "../../../../utils";
import { designBoardInputRules } from "../utils";
import { RichTextEditor } from "../../../../components/rich-text-editor";

export const DesignBoardForm = ({
  state,
  handleChange,
  errors,
}: {
  state: DesignBoard;
  handleChange: (key: keyof DesignBoard) => (value: string | string[]) => void;
  errors: Partial<Record<keyof DesignBoard, string>>;
}) => {
  const availableInputFields = state.type
    ? designBoardInputRules[state.type]
    : [];
  const fieldSet = new Set(availableInputFields);

  return (
    <Box>
      <Stack spacing={2.8}>
        <HdgTextInput
          label="Name"
          value={state.name as string}
          onChange={handleChange("name")}
          isValid={!!!errors.name}
          helperText={errors.name}
        />

        <Stack
          direction={{
            sx: "column",
            lg: "row",
          }}
          spacing={2}
        >
          {fieldSet.has("color") && (
            <DropdownSelect<ColorFamily[]>
              label="Color"
              value={state.color as ColorFamily[]}
              onChange={handleChange("color")}
              isValid={!!!errors.color}
              helperText={errors.color}
              options={colorFamilyOptions}
              multiple
            />
          )}

          {fieldSet.has("texture") && (
            <DropdownSelect
              label="Texture"
              value={state.texture as Textures[]}
              onChange={handleChange("texture")}
              isValid={!!!errors.texture}
              helperText={errors.texture}
              options={textureOptions}
              multiple
            />
          )}

          {fieldSet.has("roomType") && (
            <DropdownSelect
              label="Room Type"
              value={state.roomType as ProjectCategory[]}
              onChange={handleChange("roomType")}
              isValid={!!!errors.roomType}
              helperText={errors.roomType}
              options={projectCategoryOptions}
              multiple
            />
          )}

          {fieldSet.has("finish") && (
            <DropdownSelect
              label="Finish"
              value={state.finish as Finishes[]}
              onChange={handleChange("finish")}
              isValid={!!!errors.finish}
              helperText={errors.finish}
              options={finishOptions}
              multiple
            />
          )}

          {fieldSet.has("pattern") && (
            <DropdownSelect
              label="Pattern"
              value={state.pattern as Patterns[]}
              onChange={handleChange("pattern")}
              isValid={!!!errors.pattern}
              helperText={errors.pattern}
              options={patternOptions}
              multiple
            />
          )}
        </Stack>

        <RichTextEditor
          label="Description"
          value={(state.description as string) ?? ""}
          onChange={(html) => handleChange("description")(html)}
          helperText={errors.description}
          error={Boolean(errors.description)}
          maxChars={1000}
        />

        <RichTextEditor
          label="Why it Works?"
          value={(state.why as string) ?? ""}
          onChange={(html) => handleChange("why")(html)}
          helperText={errors.why}
          error={Boolean(errors.why)}
          maxChars={600}
        />

        <DropdownSelect
          value={state.greatFor as GreatFor}
          label="Great For"
          onChange={handleChange("greatFor")}
          isValid={!!!errors.greatFor}
          helperText={errors.greatFor}
          options={greatForOptions}
        />
      </Stack>
    </Box>
  );
};
